/**
 * WizardStepCard — expanded card for the selected wizard step.
 *
 * Invariants (designer.md / DESIGN_BRIEF):
 *   - ONE primary contained action per step ("do it" in place);
 *   - secondary "Open <sub-tab>" LINK = explicit user navigation (the only
 *     wizard element that changes tabs);
 *   - locked steps: disabled primary + annotation with the unlock reason and
 *     a link that SELECTS the unlocking step's card (never navigates);
 *   - CTA failure keeps the CTA and shows the full error (AC-A042).
 */

import React from 'react';
import {
  Alert, Box, Button, Chip, Link as MuiLink, Paper, Stack, Typography,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { CONTENT_PLAN } from '@test-kit/selectors/content-plan';
import type { WizardStepId, WizardStepView } from './wizardSteps';

export interface WizardStepCardProps {
  step: WizardStepView;
  /** Live status line, e.g. "4/10 ready topics in the backlog." */
  statusLine: string;
  primaryLabel: string;
  primaryBusy?: boolean;
  /** Extra reason the primary is disabled beyond lock state (e.g. overview mode). */
  primaryDisabledReason?: string;
  onPrimary: () => void;
  onOpenSubTab: () => void;
  /** Select another step's card (locked annotation unlock link). */
  onSelectStep: (stepId: WizardStepId) => void;
  /** Inline CTA error — CTA stays actionable (retry) while this shows. */
  error?: string | null;
  onClearError?: () => void;
}

export const WizardStepCard: React.FC<WizardStepCardProps> = ({
  step,
  statusLine,
  primaryLabel,
  primaryBusy = false,
  primaryDisabledReason,
  onPrimary,
  onOpenSubTab,
  onSelectStep,
  error,
  onClearError,
}) => {
  const primaryDisabled = step.locked || primaryBusy || Boolean(primaryDisabledReason);

  return (
    <Paper
      variant="outlined"
      data-testid={CONTENT_PLAN.WIZARD_STEP_CARD}
      sx={{ p: 2, mt: 1 }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={2} flexWrap="wrap" useFlexGap>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {step.position}. {step.label}
            </Typography>
            {step.advisory && <Chip size="small" variant="outlined" label="Optional" />}
            {step.complete && <Chip size="small" color="success" label="Done" />}
            {step.active && <Chip size="small" color="primary" variant="outlined" label="Current step" />}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {statusLine}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            disabled={primaryDisabled}
            onClick={onPrimary}
            data-testid={CONTENT_PLAN.WIZARD_STEP_PRIMARY}
          >
            {primaryBusy ? 'Working…' : primaryLabel}
          </Button>
          <Button
            variant="text"
            size="small"
            endIcon={<ArrowForwardIcon fontSize="small" />}
            onClick={onOpenSubTab}
            data-testid={CONTENT_PLAN.WIZARD_STEP_OPEN_SUBTAB}
          >
            Open {step.subTabLabel}
          </Button>
        </Stack>
      </Stack>

      {primaryDisabledReason && !step.locked && (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {primaryDisabledReason}
        </Typography>
      )}

      {step.locked && (
        <Alert
          severity="info"
          sx={{ mt: 1.5 }}
          data-testid={CONTENT_PLAN.WIZARD_STEP_LOCKED_ANNOTATION}
        >
          {step.lockReason ?? 'Complete the earlier steps first.'}
          {step.unlockedBy && (
            <>
              {' '}
              <MuiLink
                component="button"
                type="button"
                variant="body2"
                onClick={() => onSelectStep(step.unlockedBy as WizardStepId)}
                data-testid={CONTENT_PLAN.WIZARD_STEP_UNLOCK_LINK}
                sx={{ verticalAlign: 'baseline' }}
              >
                Go to that step
              </MuiLink>
            </>
          )}
        </Alert>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{ mt: 1.5 }}
          onClose={onClearError}
          data-testid={CONTENT_PLAN.WIZARD_STEP_ERROR}
        >
          {/* Full error text — the primary CTA above stays enabled for retry. */}
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {error}
          </Typography>
        </Alert>
      )}
    </Paper>
  );
};

export default WizardStepCard;
